/* ===========================================================================
   pm-require-size.js  (August 2026)
   ---------------------------------------------------------------------------
   Stops the "accidental XS" order.

   Shopify preselects the first available variant, so a PDP arrives with XS
   already chosen and Add to Cart already live. A shopper who intends to pick a
   size but gets ahead of themselves can complete the whole purchase without
   ever making a size decision. They see XS four times (picker, cart popup,
   cart drawer, confirmation email) and skim past all four, then return the
   shirt. With free returns we pay shipping twice on an order that was never
   really placed.

   Fix: on any product with a real size choice, nothing is selected on load.
   Add to Cart reads "Select a size" until the shopper picks one. Clicking it
   in that state scrolls to the size picker and flashes it instead of adding to
   the cart. Once a size is chosen the button reads "Add to cart · L", so the
   choice is echoed on the thing they're about to press.

   IMPORTANT, why this never sets the `disabled` attribute:
     1. assets/pm-back-in-stock.js treats a disabled ATC as sold out. A product
        merely awaiting a size choice is NOT sold out, and disabling the button
        would pop the restock signup form on every in-stock PDP. (That file
        also now checks for our data-pm-awaiting-size flag, belt and braces.)
     2. A disabled button gives the shopper nothing. An interceptable button
        can explain itself, which is the whole point.

   Deliberately untouched:
     - single-variant products (no options render at all)
     - products whose Size option has one value (Berber Blanket)
     - any page opened with ?variant= in the URL. Back-in-stock emails, ads and
       Google Shopping all deep-link to a specific variant. Those are
       deliberate, so honour them and stay out of the way.

   Applies to every PM product section: apparel, tee, candle, blanket,
   coordinates. The option is named "Size" across the whole catalogue, and the
   sections downcase it into data-pm-option, so [data-pm-option="size"] is a
   safe hook.
   =========================================================================== */
(function () {
  'use strict';

  var PROMPT_LABEL = 'Select a size';
  var NUDGE_TEXT = 'Choose a size first';

  function rootOf(el) {
    return (el && el.closest && el.closest('[data-section-id]')) || document;
  }

  function sizeButtons(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-pm-option="size"]'));
  }

  // A real choice means two or more size values. One value is not a decision.
  function hasSizeChoice(root) {
    return sizeButtons(root).length > 1;
  }

  function selectedSize(root) {
    var active = root.querySelector('[data-pm-option="size"].is-active');
    return active ? (active.getAttribute('data-pm-option-value') || '') : '';
  }

  function sizeOptionGroup(root) {
    var btn = root.querySelector('[data-pm-option="size"]');
    return btn ? btn.closest('.pm-pdp__option') : null;
  }

  // The buttons themselves, not the whole option block. The ring goes around
  // just the sizes so the callout's arrow has something tight to point at.
  function sizeValuesEl(root) {
    var btn = root.querySelector('[data-pm-option="size"]');
    return btn ? btn.closest('.pm-pdp__option-values') : null;
  }

  function deepLinkedVariant() {
    return /[?&]variant=\d+/.test(window.location.search || '');
  }

  // --- the nudge, injected once per size group -----------------------------
  function nudgeFor(group) {
    if (!group) return null;
    var existing = group.querySelector('[data-pm-size-nudge]');
    if (existing) return existing;
    var p = document.createElement('p');
    p.className = 'pm-size-nudge';
    p.setAttribute('data-pm-size-nudge', '');
    p.setAttribute('role', 'status');
    p.textContent = NUDGE_TEXT;
    group.appendChild(p);
    return p;
  }

  function showPrompt(root) {
    var group = sizeOptionGroup(root);
    if (!group) return;

    var nudge = nudgeFor(group);
    if (nudge) {
      // Replay the entrance animation on a repeat click: drop the class,
      // force a reflow, re-add. Without the reflow it will not replay.
      nudge.classList.remove('is-visible');
      void nudge.offsetWidth;
      nudge.classList.add('is-visible');
    }

    // The ring HOLDS until a size is picked rather than timing out. A flash
    // that fades on its own is gone by the time the eye arrives.
    var values = sizeValuesEl(root);
    if (values) values.classList.add('pm-size-flash');

    if (typeof group.scrollIntoView === 'function') {
      try {
        group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        group.scrollIntoView();
      }
    }
  }

  function hidePrompt(root) {
    var group = sizeOptionGroup(root);
    if (group) {
      var nudge = group.querySelector('[data-pm-size-nudge]');
      if (nudge) nudge.classList.remove('is-visible');
    }
    var values = sizeValuesEl(root);
    if (values) values.classList.remove('pm-size-flash');
  }

  // --- keep the button honest ----------------------------------------------
  function sync(root) {
    var atc = root.querySelector('[data-pm-atc]');
    if (!atc || !hasSizeChoice(root)) return;

    var size = selectedSize(root);

    if (!size) {
      atc.setAttribute('data-pm-awaiting-size', '');
      atc.classList.add('is-awaiting-size');
      atc.textContent = PROMPT_LABEL;
      return;
    }

    atc.removeAttribute('data-pm-awaiting-size');
    atc.classList.remove('is-awaiting-size');
    hidePrompt(root);

    // Echo the size on the button, but only when the section's own variant JS
    // has left it in the plain "Add to cart" state. Sold out and Pre-order
    // labels belong to the theme and are left exactly as they are.
    var label = (atc.textContent || '').trim();
    if (/^add to cart/i.test(label)) {
      atc.textContent = 'Add to cart · ' + size;
    }
  }

  // --- init -----------------------------------------------------------------
  function initRoot(root) {
    if (!hasSizeChoice(root)) return;
    if (deepLinkedVariant()) return; // honour ?variant= deep links
    sizeButtons(root).forEach(function (b) { b.classList.remove('is-active'); });
    sync(root);
  }

  function init() {
    var roots = [];
    document.querySelectorAll('[data-pm-atc]').forEach(function (atc) {
      var r = rootOf(atc);
      if (roots.indexOf(r) === -1) roots.push(r);
    });
    roots.forEach(initRoot);
  }

  // Should this click be blocked? Answer from the LIVE DOM, never from the
  // cached flag. If a size is genuinely selected the add must always go
  // through, whatever state the flag drifted into. This is the guarantee that
  // a missed sync can never leave the button permanently dead.
  function mustBlock(root) {
    if (!hasSizeChoice(root)) return false;
    if (selectedSize(root)) {
      sync(root); // flag drifted out of date, settle it and let the click run
      return false;
    }
    return true;
  }

  // Block the add while a size is still outstanding. Capture phase on document
  // so this runs before any handler the theme bound to the button or the form.
  document.addEventListener('click', function (e) {
    var t = e.target;
    var atc = t && t.closest ? t.closest('[data-pm-atc]') : null;
    if (!atc) return;
    var root = rootOf(atc);
    if (!mustBlock(root)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    showPrompt(root);
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    var atc = form.querySelector('[data-pm-atc]');
    if (!atc) return;
    var root = rootOf(atc);
    if (!mustBlock(root)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    showPrompt(root);
  }, true);

  // Re-sync after any option click.
  //
  // CAPTURE phase, and this is not optional. Every PM section binds its option
  // buttons with e.stopPropagation() (see handleOptionClick wiring in
  // pm-product-*.liquid). A bubble-phase listener here never fires, the
  // awaiting flag never clears, and Add to Cart stays blocked forever. That
  // exact bug shipped once. Capture runs document-down, before the button's
  // own handler, so stopPropagation cannot hide the click from us.
  //
  // The setTimeout then lets the section's updateSelectedVariant() finish
  // setting price, availability and label before we read the settled state.
  document.addEventListener('click', function (e) {
    var t = e.target;
    var opt = t && t.closest ? t.closest('[data-pm-option]') : null;
    if (!opt) return;
    var root = rootOf(opt);
    window.setTimeout(function () { sync(root); }, 0);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
